// @flow
import React from 'react';
import classNames from 'classnames';

import { type ValidationState } from 'Store/trip-information/kids/constants';
import { isKidMinor } from 'Utils/faceting/facetingUtils';
import { ADULTS_NUMBER_MAX } from 'Utils/faceting/constants';
import { InputNumber } from 'Components/frameworks/form-inputs/input-number/InputNumber.jsx';
import { InputDate } from 'Components/frameworks/form-inputs/input-date/InputDate.jsx';
import { InputSwitch } from 'Components/frameworks/form-inputs/input-switch/InputSwitch.jsx';
import { FormFeedback } from 'Components/frameworks/form-feedback/FormFeedback.jsx';
import { hasKidWithoutAValidBirthdate } from 'Utils/faceting/kidsValidationStateUtils';

type Props = {
  addChildcareLabel: string,
  adultsLabel: string,
  adultsNumber: number,
  minAdultsNumber?: number,
  changeKid: (number, Kid) => void,
  changeKidsNumber: number => void,
  dateFormat: string,
  dayPlaceholder: string,
  informationLabel?: string,
  kids: $ReadOnlyArray<any>,
  kidsAgeLabel: string,
  kidsAgeLimitLabel: string,
  kidsLabel: string,
  kidsValidationState: Array<ValidationState>,
  validateKidsBirthdate: (number, Kid) => void,
  label: string,
  monthPlaceholder: string,
  setAdultsNumber: number => void,
  yearPlaceholder: string,
  theme?: 'shrink' | 'default',
  birthdaysErrorMessage?: string,
};

type DefaultProps = {
  informationLabel: string,
  theme: 'default',
  birthdaysErrorMessage: string,
};

export class FilterWho extends React.PureComponent<Props> {
  static defaultProps: DefaultProps = {
    informationLabel: '',
    theme: 'default',
    birthdaysErrorMessage: '',
  };

  handleKidsNumber = (name: string, value: number): void => {
    const { changeKidsNumber } = this.props;
    if (changeKidsNumber) {
      changeKidsNumber(value);
    }
  };

  handleKidAge = async (index: number, kid: *) => {
    const { changeKid, validateKidsBirthdate } = this.props;
    // check date validity
    if (kid.birthdate) {
      await validateKidsBirthdate(index, kid);
      // $FlowFixMe
      if (isKidMinor(kid.birthdate)) {
        changeKid(index, kid);
      }
    }
  };

  getMaxAdultsAvailable() {
    const {
      kids: { length: kidsNumber },
    } = this.props;
    return ADULTS_NUMBER_MAX - kidsNumber;
  }

  getMaxKidsAvailable() {
    const { adultsNumber } = this.props;
    return ADULTS_NUMBER_MAX - adultsNumber;
  }

  render() {
    const {
      addChildcareLabel,
      adultsLabel,
      adultsNumber,
      changeKid,
      dateFormat,
      dayPlaceholder,
      informationLabel,
      kids,
      kidsAgeLabel,
      kidsAgeLimitLabel,
      kidsLabel,
      kidsValidationState,
      label,
      minAdultsNumber = 0,
      monthPlaceholder,
      setAdultsNumber,
      theme,
      yearPlaceholder,
      birthdaysErrorMessage,
    } = this.props;

    const isShrink = theme === 'shrink';

    return (
      <div data-testid="FilterWho">
        <div className="px-4 py-6">
          <div className="mb-6 text-lg font-bold lg:text-center">{label}</div>
          <div className="lg:flex lg:items-center text-left">
            <InputNumber
              className="my-6 lg:flex-auto lg:mx-6 lg:my-0 lg:w-1/2"
              hasColumnLayout={isShrink}
              label={adultsLabel}
              value={adultsNumber}
              onChange={(name, value) => {
                setAdultsNumber(value || 0);
              }}
              min={minAdultsNumber}
              max={this.getMaxAdultsAvailable()}
              dataTestId="FilterWho-Adults"
              attendeeType="adults"
              ariaLabel={adultsLabel}
            />
            <InputNumber
              className="my-6 lg:flex-auto lg:mx-6 my-4 lg:my-0 lg:w-1/2"
              hasColumnLayout={isShrink}
              label={kidsLabel}
              notes={kidsAgeLimitLabel}
              value={kids.length}
              onChange={this.handleKidsNumber}
              max={this.getMaxKidsAvailable()}
              dataTestId="FilterWho-Children"
              attendeeType="kids"
              ariaLabel={kidsLabel}
            />
          </div>
        </div>

        {kids.length > 0 && (
          <div className="p-4 bg-gray-lighter border-blue border-t">
            <div className="mb-3 text-lg">{kidsAgeLabel}</div>
            {informationLabel ? (
              <div className="mb-3 italic text-base">{informationLabel}</div>
            ) : null}
            {hasKidWithoutAValidBirthdate(kidsValidationState) &&
              birthdaysErrorMessage && <FormFeedback isError message={birthdaysErrorMessage} />}

            <div
              className={classNames('lg:flex lg:flex-wrap lg:-m-2', {
                'flex-col': theme === 'shrink',
              })}
            >
              {kids.map((kid, index) => (
                <div
                  key={String(kid.birthdate) + String(index)}
                  className={classNames('mb-6 lg:mb-0 lg:p-2 lg:pb-4', {
                    'lg:w-1/2': theme === 'default',
                  })}
                >
                  <InputDate
                    className="mb-3"
                    dateFormat={dateFormat}
                    value={kid.birthdate}
                    placeholderDay={dayPlaceholder}
                    placeholderMonth={monthPlaceholder}
                    placeholderYear={yearPlaceholder}
                    validationState={kidsValidationState[index]}
                    onChange={(name, birthdate) => {
                      return this.handleKidAge(index, { ...kid, birthdate });
                    }}
                  />
                  {typeof kid.childcareIncluded !== 'undefined' ? (
                    <InputSwitch
                      // $FlowFixMe
                      value={!!kid.childcareIncluded}
                      isDisabled={kidsValidationState[index] !== 'IS_VALID'}
                      // $FlowFixMe
                      isChecked={!!kid.childcareIncluded}
                      label={
                        <span className="font-bold text-blue text-sm">{addChildcareLabel}</span>
                      }
                      onChange={(name, childcareIncluded) => {
                        changeKid(index, { ...kid, childcareIncluded });
                      }}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}
